<script setup lang="ts">
import { useLongPressTooltip } from "@/composable/useLongPressTooltip";
import { watch, onMounted, onBeforeUnmount, ref } from "vue";
import { Facade, DraggerType } from "@/drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import { Logger } from "@/logger";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const TextInput = ref("");
const TextInputHTML = ref<HTMLInputElement | null>(null);
const { refHook } = useLongPressTooltip();

function setActivePhotoMarkerNumber() {
  if (!Facade.isInitialized) return;
  const num = parseInt(TextInput.value) || 1;
  Facade.photoMarkers.currentNumber = num;
}

function incrementBtnHandler(offset: number) {
  const current = parseInt(TextInput.value) || 0;
  if (current === 0 && offset < 0) {
    offset = 0;
  }
  TextInput.value = (current + offset).toString();
  setActivePhotoMarkerNumber();
}

function syncFromFacade() {
  if (!Facade.isInitialized) return;
  TextInput.value = Facade.photoMarkers.currentNumber.toString();
}

watch(
  () => TextInput.value,
  (newValue) => {
    if (!newValue) TextInput.value = "0";
  },
);

let unsubscribeFileOpen: (() => void) | undefined;
let unsubscribePhotoMarkers: (() => void) | undefined;

function subscribeToChanges() {
  if (unsubscribePhotoMarkers) return;
  unsubscribePhotoMarkers = Facade.photoMarkers.onChange(() => {
    syncFromFacade();
  });
}

onMounted(() => {
  // Subscribe to file-open events so we sync after markers are scanned,
  // even if Facade wasn't initialized or file wasn't open at mount time.
  unsubscribeFileOpen = Facade.onFileOpen(() => {
    syncFromFacade();
    subscribeToChanges();
  });

  // If Facade is already initialized, subscribe to ongoing changes now
  if (Facade.isInitialized) {
    syncFromFacade();
    subscribeToChanges();
  }
});

onBeforeUnmount(() => {
  unsubscribeFileOpen?.();
  unsubscribePhotoMarkers?.();
});

function plus() {
  Logger.info(`PhotoMarkerInput.vue : plus photo number is ${TextInput.value}`);
  incrementBtnHandler(1);
}

function minus() {
  Logger.info(
    `PhotoMarkerInput.vue : minus photo number is ${TextInput.value}`,
  );
  incrementBtnHandler(-1);
}

watch(
  () => DraggerMenuState.activeDragger.name,
  (newName, oldName) => {
    if (newName == DraggerType.PhotoMarker && newName != oldName) {
      setActivePhotoMarkerNumber();
    }
  },
);
</script>
<template>
  <div
    class="canvas-option-wrapper d-flex align-items-center overflow-hidden"
    ref="refHook"
    :data-bs-content="$t('canvas:tools.photomarker.tooltip.text')"
    :data-bs-title="$t('canvas:tools.photomarker.tooltip.title')"
  >
    <button
      type="button"
      class="btn btn-white text-white p-1 side-button"
      @click="minus()"
    >
      <phosphor-icon name="minus" :size="12" weight="bold" class="opacify-70" />
    </button>
    <input
      v-model="TextInput"
      ref="TextInputHTML"
      @input="setActivePhotoMarkerNumber()"
      @keydown.enter.prevent="($event.target as HTMLInputElement).blur()"
      type="number"
      inputmode="numeric"
      enterkeyhint="done"
      class="form-control dr-photo-marker-input"
      placeholder="123"
    />
    <button
      type="button"
      class="btn btn-white text-white p-1 side-button"
      @click="plus()"
    >
      <phosphor-icon name="plus" :size="12" weight="bold" class="opacify-70" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.side-button {
  width: 21px;
  height: 100%;
  &:hover {
    background-color: var(--canvas-bg-hover);
  }
}
.canvas-option-wrapper {
  height: 30px;
  border-radius: 8px;
  background-color: var(--canvas-bg);
}

.dr-photo-marker-input {
  width: 42px;
  height: 100%;
  font-size: 14px !important;
  text-align: center;

  border-radius: 0 !important;
}

.form-control {
  background-color: var(--canvas-bg);
  padding: 0.375rem 0.1rem;
  border: none !important;
  box-shadow: none !important;
  color: white !important;
  &:hover {
    background-color: var(--canvas-bg-hover);
  }
  &:focus {
    background-color: var(--canvas-bg) !important;
    color: white !important;
  }
}
/** Remove default + - on desktop */
/* Chrome, Safari, Edge, Opera */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
